/**
 * Messages client pour tarifications indisponibles (tous assureurs).
 */
export type PricingUnavailableReason =
  | 'api_no_result'
  | 'api_error'
  | 'empty_response'
  | 'no_matching_formula'
  | 'no_matching_product'
  | 'incomplete_form'
  | 'invalid_profile'
  | 'commission'
  | 'technical_error';

export class PricingFeedbackUtil {

  private static readonly TNS_PRO_PRODUCTS = new Set([
    'VITAMT3', 'SAKIT3', 'VITAMH3', 'SAKIH3'
  ]);

  private static readonly APIVIA_M3_PRODUCTS = new Set([
    'VITAMM3', 'SAKIM3', 'VITAMH3', 'SAKIH3'
  ]);

  static buildProductLabel(contractName?: string, formule?: string, assurance?: string): string {
    const label = [contractName, formule].filter((p) => !!p && String(p).trim()).join(' — ');
    if (label) {
      return label;
    }
    return (assurance || 'Produit').toString().trim();
  }

  static reasonToDefaultMessage(reason: PricingUnavailableReason): string {
    switch (reason) {
      case 'api_no_result':
        return 'L\'assureur n\'a renvoyé aucun tarif pour ce profil.';
      case 'api_error':
        return 'L\'assureur a refusé ou rejeté la demande de tarification.';
      case 'empty_response':
        return 'La réponse ne contient pas de montant de cotisation.';
      case 'no_matching_formula':
        return 'La formule demandée n\'a pas été trouvée dans la réponse de l\'assureur.';
      case 'no_matching_product':
        return 'Le produit ne correspond pas aux offres tarifables pour ce profil.';
      case 'incomplete_form':
        return 'Informations personnelles ou garanties incomplètes dans le formulaire.';
      case 'invalid_profile':
        return 'Profil non éligible (âge, régime, code postal, composition familiale, etc.).';
      case 'commission':
        return 'Commission non reconnue ou non disponible pour ce produit.';
      case 'technical_error':
      default:
        return 'Erreur technique lors de l\'appel à l\'assureur.';
    }
  }

  /**
   * Transforme un message brut API en explication lisible pour le client.
   */
  static interpretMessage(raw: string, insurerHint?: string, productCode?: string): string {
    const msg = (raw || '').toString().trim();
    if (!msg) {
      return PricingFeedbackUtil.reasonToDefaultMessage('empty_response');
    }

    const lower = msg.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const code = (productCode || '').toUpperCase();

    if (
      lower.includes('aucun resultat pour cette requete') ||
      lower.includes('aucun resultat') ||
      lower.includes('no result')
    ) {
      if (PricingFeedbackUtil.TNS_PRO_PRODUCTS.has(code)) {
        return 'Ce produit « Pro » est réservé aux indépendants (TNS). Choisissez le régime « Sécurité Sociale des Indépendants » ou une catégorie artisan/commerçant/libéral, puis relancez la tarification.';
      }
      if (code === 'SAKIM3') {
        return 'Sakifo³ (SAKIM3) : Apivia ne tarifie pas ce profil pour la formule demandée (âge à la date d\'effet, niveau ou régime). Vitamin³ peut répondre avec les mêmes données — essayez un niveau inférieur ou vérifiez l\'éligibilité Sakifo auprès d\'Apivia.';
      }
      if (code === 'VITAMM3') {
        return 'Vitamin³ (VITAMM3) : combinaison non tarifable pour ce profil. Vérifiez la formule, le régime (TNS si indépendant), la date d\'effet et l\'âge à l\'effet.';
      }
      if (PricingFeedbackUtil.APIVIA_M3_PRODUCTS.has(code)) {
        return 'Produit Apivia non tarifé pour ce profil : vérifiez la formule, le régime social et l\'âge à la date d\'effet.';
      }
      return 'Combinaison non tarifable : vérifiez le produit, la formule, le régime obligatoire, la date d\'effet et la composition familiale (conjoint/enfants).';
    }

    if (lower.includes('non eligible') || lower.includes('ineligible') || lower.includes('pas eligible')) {
      return 'Profil non éligible selon les critères de l\'assureur (âge, régime, situation professionnelle ou zone géographique).';
    }

    if (lower.includes('commission') && (lower.includes('inval') || lower.includes('inconn') || lower.includes('non'))) {
      return 'La commission sélectionnée n\'est pas acceptée pour ce produit. Essayez une autre commission dans la liste.';
    }

    if (lower.includes('date') && (lower.includes('inval') || lower.includes('effet'))) {
      return 'Date d\'effet ou date de naissance invalide ou hors plage autorisée.';
    }

    if (lower.includes('regime') || lower.includes('regime social')) {
      return 'Régime obligatoire incompatible avec ce produit. Adaptez le régime du formulaire (TNS, général, Alsace-Moselle, etc.).';
    }

    if (
      lower.includes('donnees envoyees') ||
      lower.includes('ne sont pas correctes') ||
      lower.includes('donnees incorrectes')
    ) {
      return 'Données refusées par l\'assureur : vérifiez le CSP du conjoint (employés/cadres pour Santé Select Alptis), les dates de naissance, le régime et la composition familiale.';
    }

    if (lower.includes('conjoint') && (lower.includes('csp') || lower.includes('categorie'))) {
      return 'Le conjoint n\'est pas éligible avec la catégorie professionnelle indiquée pour ce produit.';
    }

    if (lower.includes('code postal') || lower.includes('codepostal') || lower.includes('departement')) {
      return 'Code postal non couvert ou invalide pour ce produit.';
    }

    if (lower.includes('produit') && (lower.includes('inconn') || lower.includes('inval') || lower.includes('introuvable'))) {
      return 'Code produit non reconnu par l\'assureur pour ce contrat.';
    }

    if (lower.includes('formule') && (lower.includes('inconn') || lower.includes('inval') || lower.includes('introuvable'))) {
      return 'Niveau ou formule non disponible pour ce produit et ce profil.';
    }

    if (lower.includes('http failure') || lower.includes('500 ok') || lower.includes('unknown error')) {
      return 'Service assureur temporairement indisponible. Réessayez dans quelques instants.';
    }

    if (lower.includes('timeout') || lower.includes('timed out')) {
      return 'Délai dépassé : l\'assureur n\'a pas répondu à temps.';
    }

    const insurer = (insurerHint || '').trim();
    if (insurer && !msg.toLowerCase().startsWith(insurer.toLowerCase())) {
      return msg;
    }
    return msg;
  }

  /** Extrait le premier message texte d'une réponse JSON assureur. */
  static extractListMessage(response: any): string | null {
    if (!response) {
      return null;
    }
    const list = response.list ?? response.List ?? response.messages;
    if (Array.isArray(list) && list.length > 0) {
      const first = list[0];
      if (typeof first === 'string' && first.trim()) {
        return first.trim();
      }
      if (first && typeof first === 'object') {
        const t = (first.message ?? first.description ?? first.detail ?? first.libelle ?? '').toString().trim();
        if (t) {
          return t;
        }
      }
    }
    const m = (response.message ?? response.detail ?? response.error ?? '').toString().trim();
    return m || null;
  }

  static isNoResultResponse(response: any): boolean {
    const msg = PricingFeedbackUtil.extractListMessage(response);
    if (!msg) {
      return false;
    }
    const lower = msg.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return lower.includes('aucun resultat');
  }
}
